import * as Sentry from '@sentry/react';
import { z } from 'zod';

import { ROLES, type Role } from '@/types';

import { isPasswordValid } from './password-rules';

// Canonical role set now lives in the `types` layer (see src/types/roles.ts);
// re-exported here for back-compat with existing auth-feature imports.
export const ADMIN_ROLES = ROLES;

const isKnownRole = (value: unknown): value is Role =>
  typeof value === 'string' && (ROLES as readonly string[]).includes(value);

/** Unknown role values already reported this session. `userSchema` is parsed on every login, every
 *  `/auth/admin/me`, and every TanStack refetch (`refetchOnWindowFocus`), so without this one
 *  drifted account would emit an unbounded stream of identical Sentry events. */
const reportedUnknownRoles = new Set<string>();

/** Test-only: clears the once-per-value Sentry de-dup so cases don't leak into each other. */
export function resetUnknownRoleReports(): void {
  reportedUnknownRoles.clear();
}

/**
 * The wire `role`, degraded to `null` for anything this build doesn't recognise.
 *
 * A strict `z.enum` here is a single point of failure for the whole app: when the backend renamed
 * `customer_support_logistics` → `operational_associate`, `/auth/admin/me` stopped parsing and
 * every user with that role was locked out entirely. Tolerating the unknown value keeps the
 * session usable — `RequireRole` denies every gated module on a null role and the backend remains
 * the real authority (ADR-0001) — while Sentry gets a loud, greppable drift signal (ADR-0020).
 * Accepts `unknown` rather than `z.string()` so a missing or non-string role degrades too, instead
 * of reintroducing the very lockout this exists to prevent. See ADR-0021.
 */
const roleSchema = z.unknown().transform((value) => {
  if (isKnownRole(value)) return value;
  const key = String(value);
  if (!reportedUnknownRoles.has(key)) {
    reportedUnknownRoles.add(key);
    Sentry.captureMessage('Unknown admin role in auth user payload', {
      // `parseContract` (ADR-0020) keys this tag by endpoint; the auth user payload is shared by
      // login, otp/verify and /auth/admin/me, so it is keyed by payload+field instead.
      tags: { contract_drift: 'auth/user.role' },
      extra: { role: value },
    });
  }
  return null;
});

export const phoneSchema = z.string().regex(/^\d{10}$/, 'Enter a valid 10-digit US phone number');
export const otpSchema = z.string().regex(/^\d{6}$/, 'Enter the 6-digit code');

export const userSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: roleSchema,
});

/** `login/otp/verify` answers with one of two 200 shapes: a full session for an activated account, or a
 *  single-purpose `passwordSetToken` for a first login that has no password yet (ADO 117536). Parsed as
 *  a union so an unrecognised third shape fails loudly here instead of surfacing as
 *  `userSchema.parse(undefined)` behind a generic error toast. */
export const loginVerifySchema = z.union([
  z.object({ accessToken: z.string(), user: userSchema }),
  z.object({ passwordSetToken: z.string() }),
]);

export const loginPasswordSchema = z.object({
  phone: phoneSchema,
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional().default(false),
});

export const sendOtpSchema = z.object({
  phone: phoneSchema,
  rememberMe: z.boolean().optional().default(false),
});

export const newPasswordSchema = z
  .object({
    newPassword: z.string().refine(isPasswordValid, 'Password does not meet the requirements'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });
